import {
  MapTrifold,
  House,
  GraduationCap,
  NotePencil,
  Trophy,
  ChartLineUp,
  UserCircle,
  ArrowRight,
  CaretDown,
} from "@phosphor-icons/react";
import { SectionShell } from "../../styleguide/Primitives";
import { Showcase } from "../../library/LibraryPrimitives";
import { AreaNode } from "../diagrams";
import { cn } from "@/lib/utils";

const RELATIONSHIPS = [
  { from: "Home", to: "Learn", why: "Continue learning picks up the next lesson" },
  { from: "Home", to: "Homework", why: "Due-today nudges flow straight to the task" },
  { from: "Learn", to: "Progress", why: "Finishing a topic grows its mastery" },
  { from: "Learn", to: "Rewards", why: "Effort earns XP, badges & streaks" },
  { from: "Homework", to: "Progress", why: "Scores become part of the journey" },
  { from: "Any area", to: "Profile", why: "Year group & accessibility shape it all" },
];

const HIERARCHY = ["Portal", "Area", "Learning mode", "Activity", "Question / Step"];

export function NavMapSection() {
  return (
    <SectionShell
      id="map"
      eyebrow="Navigation Map"
      eyebrowIcon={MapTrifold}
      title="Six calm areas, one warm hub"
      description="Six top-level areas are always within reach. Home is the hub children return to; everything else is one gentle tap away. There are no dead ends — every path offers a kind next step."
    >
      <div className="space-y-8">
        <Showcase title="Home is the hub" tone="muted">
          <div className="grid gap-4 lg:grid-cols-3">
            <AreaNode icon={GraduationCap} label="Learn" purpose="Discover & practice" tone="blue" />
            <AreaNode icon={House} label="Home" purpose="Continue & build habits" tone="orange" className="ring-4 ring-primary/15 lg:row-span-1" />
            <AreaNode icon={NotePencil} label="Homework" purpose="Tasks from grown-ups" tone="teal" />
            <AreaNode icon={ChartLineUp} label="Progress" purpose="See growth over time" tone="blue" />
            <AreaNode icon={Trophy} label="Rewards" purpose="Celebrate effort" tone="yellow" />
            <AreaNode icon={UserCircle} label="Profile" purpose="Avatar & settings" tone="neutral" />
          </div>
        </Showcase>

        <div className="grid gap-5 lg:grid-cols-2">
          <Showcase title="How areas relate">
            <div className="space-y-2.5">
              {RELATIONSHIPS.map((r) => (
                <div key={r.from + r.to} className="flex flex-wrap items-center gap-2 rounded-2xl bg-muted/40 p-3">
                  <span className="rounded-full bg-card px-3 py-1.5 text-sm font-bold shadow-soft">{r.from}</span>
                  <ArrowRight size={16} weight="bold" className="text-muted-foreground" />
                  <span className="rounded-full bg-primary/12 px-3 py-1.5 text-sm font-bold text-primary-strong">{r.to}</span>
                  <span className="w-full text-sm font-medium text-muted-foreground sm:w-auto sm:flex-1">{r.why}</span>
                </div>
              ))}
            </div>
          </Showcase>

          <Showcase title="Screen hierarchy" description="How depth is organised">
            <div className="flex flex-col gap-2">
              {HIERARCHY.map((h, i) => (
                <div key={h} className="flex items-center gap-3" style={{ paddingLeft: `${i * 18}px` }}>
                  <span className={cn("flex h-9 items-center rounded-2xl px-4 font-bold shadow-soft", i === 0 ? "bg-primary text-primary-foreground" : "bg-card")}>{h}</span>
                  {i < HIERARCHY.length - 1 && <CaretDown size={16} weight="bold" className="text-muted-foreground" />}
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm font-medium text-muted-foreground">Children rarely go deeper than 3 taps. Navigation stays shallow, predictable and reversible.</p>
          </Showcase>
        </div>

        <div className="flex items-start gap-3 rounded-3xl border border-border bg-accent/50 p-5">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><House size={22} weight="fill" /></span>
          <p className="font-medium text-foreground"><strong>Navigation pattern:</strong> Years 1–4 use a big <strong>bottom tab bar</strong> (Home · Learn · Rewards · Me); Years 5–6 can switch to a tidier <strong>sidebar</strong>. A persistent Home is always one tap away.</p>
        </div>
      </div>
    </SectionShell>
  );
}

export default NavMapSection;
